/**
 * BotSpot Web Dashboard - Bot Detail Page
 *
 * Displays detailed information about a specific bot with controls.
 */

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getBot } from "../api/bots";
import { getErrorMessage } from "../api/client";
import type { Bot } from "../api/types";
import { BotStatus } from "../api/types";
import { BotInfo } from "../components/BotInfo";
import { BotChat } from "../components/BotChat";
import { BotSteps } from "../components/BotSteps";
import { BotControls } from "../components/BotControls";
import { BotMetrics } from "../components/BotMetrics";
import { BotLogs } from "../components/BotLogs";
import { useAgentSteps } from "../hooks/useAgentSteps";
import { PIXEL, MONO } from "../theme";

/**
 * Bot Detail Page Component
 *
 * Fetches and displays detailed information about a specific bot.
 */
export function BotDetailPage() {
  const { botId } = useParams<{ botId: string }>();
  const navigate = useNavigate();

  const [bot, setBot] = useState<Bot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Real-time agent step streaming (active only when bot is RUNNING)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { steps, connected, clearSteps } = useAgentSteps(
    botId ?? null,
    bot?.status === BotStatus.RUNNING
  );

  /**
   * Fetch bot details
   */
  const fetchBot = async () => {
    if (!botId) {
      setError("Bot ID is required");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await getBot(botId);
      setBot(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch bot on mount and set up polling
   */
  useEffect(() => {
    fetchBot();

    // Poll for updates every 5 seconds
    const interval = setInterval(() => {
      if (botId) {
        getBot(botId)
          .then((data) => setBot(data))
          .catch(() => {
            // Silent fail on polling errors
          });
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [botId]);

  /**
   * Handle bot updated
   */
  const handleBotUpdated = (updatedBot: Bot) => {
    setBot(updatedBot);
  };

  /**
   * Handle bot deleted
   */
  const handleBotDeleted = () => {
    navigate("/dashboard");
  };

  /**
   * Render loading state
   */
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}>🔄</div>
          <p style={styles.loadingText}>Loading bot details...</p>
        </div>
      </div>
    );
  }

  /**
   * Render error state
   */
  if (error || !bot) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <div style={styles.errorIcon}>❌</div>
          <h2 style={styles.errorTitle}>Error Loading Bot</h2>
          <p style={styles.errorText}>{error || "Bot not found"}</p>
          <button style={styles.backButton} onClick={() => navigate("/dashboard")}>
            ← Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  /**
   * Render bot details
   */
  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.pageHeader}>
        <button style={styles.backButton} onClick={() => navigate("/dashboard")}>
          ← Back to Dashboard
        </button>
      </div>

      {/* Bot Info */}
      <div style={styles.section}>
        <BotInfo bot={bot} />
      </div>

      {/* Bot Chat */}
      <div style={styles.section}>
        <BotChat botId={bot.id} botStatus={bot.status} />
      </div>

      {/* Agent Steps (real-time SSE) */}
      <div style={styles.section}>
        <BotSteps steps={steps} connected={connected} />
      </div>

      {/* Bot Controls */}
      <div style={styles.section}>
        <BotControls
          bot={bot}
          onBotUpdated={handleBotUpdated}
          onBotDeleted={handleBotDeleted}
        />
      </div>

      {/* Bot Metrics */}
      <div style={styles.section}>
        <BotMetrics botId={bot.id} />
      </div>

      {/* Bot Logs */}
      <div style={styles.section}>
        <BotLogs botId={bot.id} />
      </div>
    </div>
  );
}

/**
 * Inline styles
 */
const styles: Record<string, React.CSSProperties> = {
  container: {
    width: "100%",
    maxWidth: "1200px",
    margin: "0 auto",
  },
  pageHeader: {
    marginBottom: "2rem",
  },
  backButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.5rem",
    padding: "10px 16px",
    backgroundColor: "transparent",
    border: `1px solid rgba(0,106,167,0.3)`,
    color: "rgba(255,255,255,0.6)",
    fontFamily: PIXEL,
    fontSize: "0.55rem",
    fontWeight: 400,
    cursor: "pointer",
    transition: "all 0.2s",
    letterSpacing: "0.08em",
  },
  section: {
    marginBottom: "2rem",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "400px",
    gap: "1rem",
  },
  spinner: {
    fontSize: "3rem",
    animation: "spin 1s linear infinite",
  },
  loadingText: {
    fontFamily: MONO,
    fontSize: "0.9rem",
    color: "rgba(255,255,255,0.5)",
    margin: 0,
    letterSpacing: "0.05em",
  },
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "400px",
    gap: "1rem",
    textAlign: "center",
  },
  errorIcon: {
    fontSize: "4rem",
  },
  errorTitle: {
    margin: 0,
    fontFamily: PIXEL,
    fontSize: "0.8rem",
    fontWeight: 400,
    color: "rgba(255,255,255,0.87)",
    letterSpacing: "0.06em",
  },
  errorText: {
    margin: 0,
    fontFamily: MONO,
    fontSize: "0.9rem",
    color: "rgba(255,255,255,0.5)",
    maxWidth: "500px",
  },
};
